import { useCallback, useMemo } from 'react';

import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useResolveUsers } from '~/hooks/useResolveUsers';
import { slices } from '~/redux/slices';
import { useAppDispatch, useAppSelector } from '~/rootState';
import { PilotId } from '~/types/PilotId';
import { User } from '~/types/User';

import { ESignatureIntegrationUsers } from '../../types';
import { useMapResolvedUsers } from './useMapResolvedUsers';

export const useIntegration = (
  IntegrationUserData: ESignatureIntegrationUsers | undefined,
  searchKeyword: string | undefined,
) => {
  const data = IntegrationUserData ?? [];
  const dispatch = useAppDispatch();
  const selectedUsers = useAppSelector(
    (state) => state.esignatureIntegration.selectedUsers,
  );
  const currentUser = useCurrentUser();

  const userIds = useMemo(
    () => data.map((d) => d.attributes.internal_user_id),
    [data],
  );
  const { users: fetchedUsers, isLoading } = useResolveUsers({
    userIds,
    skip: !userIds.length,
  });
  const hasLoaded = useMemo(
    () => !isLoading && fetchedUsers !== undefined,
    [isLoading, fetchedUsers],
  );

  const resolvedUsers = useMemo(
    () => (hasLoaded ? (fetchedUsers ?? {}) : undefined),
    [hasLoaded, fetchedUsers],
  );
  const mapResolvedUsers = useMapResolvedUsers();
  const displayedUsers = useMemo(
    () =>
      mapResolvedUsers(
        currentUser,
        data,
        resolvedUsers as Record<PilotId, User>,
        searchKeyword,
      ),
    [data, resolvedUsers, searchKeyword, currentUser?.id],
  );

  const toggleUser = useCallback(
    (userId: string) => {
      dispatch(slices.esignature.actions.toggleUserSelection(userId));
    },
    [dispatch],
  );

  const apiUsersCount = resolvedUsers ? Object.keys(resolvedUsers).length : 0;
  const integrationState = useMemo(() => {
    if (isLoading || !hasLoaded) return null;
    if (displayedUsers.length === 0) return 'notFound' as const;
    return null;
  }, [apiUsersCount, displayedUsers]);

  return {
    selectedUsers,
    toggleUser,
    displayedUsers,
    integrationState,
    isLoading,
    resolvedUsers,
  };
};
